import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Plus, Trash2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { ContactAdditionalField, ContactChannel } from "schema"

type ContactChannelValue = (typeof ContactChannel)[keyof typeof ContactChannel]

type ContactRequirementValue = {
  contactChannel: ContactChannelValue
  additionalFields: ContactAdditionalField[]
}

type Props = {
  value: ContactRequirementValue
  onChange: (value: ContactRequirementValue) => void
}

export function ContactRequirementFields({ value, onChange }: Props) {
  const { t } = useTranslation()

  const contactChannel = value.contactChannel ?? ContactChannel.PhoneOrEmail
  const additionalFields = value.additionalFields ?? []

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="contact-channel-select">
          {t("capabilities.contactRequirement.contactChannel.label")}
        </Label>
        <Select
          value={contactChannel}
          onValueChange={(channel: ContactChannelValue) =>
            onChange({
              ...value,
              contactChannel: channel,
              additionalFields: channel === ContactChannel.None ? [] : additionalFields,
            })
          }
        >
          <SelectTrigger id="contact-channel-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(ContactChannel).map((channel) => (
              <SelectItem key={channel} value={channel}>
                {t(`capabilities.contactRequirement.contactChannel.options.${channel}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {t("capabilities.contactRequirement.contactChannel.description")}
        </span>
      </div>
      {contactChannel !== ContactChannel.None && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">
              {t("capabilities.contactRequirement.additionalFields.label")}
            </span>
            <span className="text-sm text-muted-foreground">
              {t("capabilities.contactRequirement.additionalFields.description")}
            </span>
          </div>
          {additionalFields.map((field, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={field.label}
                onChange={(e) => {
                  const next = [...additionalFields]
                  next[index] = { ...field, label: e.target.value }
                  onChange({ ...value, additionalFields: next })
                }}
                placeholder={t("capabilities.contactRequirement.additionalFields.placeholder")}
              />
              <label className="flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground">
                <Checkbox
                  checked={field.required}
                  onCheckedChange={(checked) => {
                    const next = [...additionalFields]
                    next[index] = { ...field, required: checked === true }
                    onChange({ ...value, additionalFields: next })
                  }}
                />
                {t("capabilities.contactRequirement.additionalFields.requiredLabel")}
              </label>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() =>
                  onChange({
                    ...value,
                    additionalFields: additionalFields.filter((_, i) => i !== index),
                  })
                }
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() =>
              onChange({
                ...value,
                additionalFields: [...additionalFields, { label: "", required: true }],
              })
            }
          >
            <Plus className="w-4 h-4" />
            <span>{t("capabilities.contactRequirement.additionalFields.addButton")}</span>
          </Button>
        </div>
      )}
    </div>
  )
}
